import { Box, Container, Typography } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import React, { useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useAuth } from 'react-oidc-context'
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import { muiTheme } from '../Theme'
import ErrorPage from '../components/ErrorPage'
import Logout from '../components/logout/Logout'
import HeaderNavigation from '../components/navigation/HeaderNavigation/HeaderNavigation'
import { AppAPIClient } from '../lib/index'
import PerksListing from '../pages/PerksListing'
import UnauthenticatedLanding from '../pages/UnauthenticatedLanding'
import type { WhoAmIPermissions } from '../types'

// Access vite-defined global constants
declare const BASENAME: string

const App = () => {
  const auth = useAuth()
  const [api, setApi] = useState(new AppAPIClient(auth))
  const [userPermissions, setUserPermissions] =
    useState<WhoAmIPermissions | null>(null)

  // Removed automatic redirect - users will see landing page instead

  // Update API client only when auth is complete and has a token
  useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading && auth.user?.access_token) {
      setApi(new AppAPIClient(auth))
    }
  }, [auth])

  useEffect(() => {
    const fetchUserPermissions = async () => {
      try {
        const { data } = await api.v1whoAmI.get()
        setUserPermissions(data)
      } catch (error) {
        console.error('Error fetching user permissions:', error)
        setUserPermissions(null)
      }
    }

    // Only fetch permissions when we have a valid API client with authentication
    if (
      auth.isAuthenticated &&
      !auth.isLoading &&
      !auth.error &&
      auth.user?.access_token &&
      api.auth?.user?.access_token
    ) {
      fetchUserPermissions()
    }
  }, [
    api,
    auth.isAuthenticated,
    auth.isLoading,
    auth.error,
    auth.user?.access_token,
  ])

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Router
        basename={BASENAME}
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <ErrorBoundary
          FallbackComponent={({ resetErrorBoundary }) => (
            <ErrorPage
              message="Something unexpected happened. Please try refreshing the page."
              onRetry={resetErrorBoundary}
              linkMenu={true}
            />
          )}
          onError={(error, errorInfo) => {
            console.error('App-level error caught:', error, errorInfo)
          }}
        >
          {/* Show HeaderNavigation only for authenticated users */}
          {auth.isAuthenticated && (
            <HeaderNavigation userPermissions={userPermissions} />
          )}

          {/* Show different content based on authentication state */}
          {auth.isLoading ? (
            <Container maxWidth="lg">
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="h5">Loading...</Typography>
              </Box>
            </Container>
          ) : !auth.isAuthenticated ? (
            <UnauthenticatedLanding />
          ) : (
            <Container maxWidth="lg">
              <Box sx={{ py: 4 }}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <PerksListing
                        canReadPerks={userPermissions?.canReadPerks ?? null}
                      />
                    }
                  />
                  {/* by navigating to PerksListing, we can show a single URL navigable modal */}
                  <Route
                    path="/perk/:id"
                    element={
                      <PerksListing
                        canReadPerks={userPermissions?.canReadPerks ?? null}
                      />
                    }
                  />
                  <Route path="/logout" element={<Logout />} />
                  <Route
                    path="*"
                    element={
                      <Box textAlign="center" sx={{ mt: 4 }}>
                        <Typography variant="h4">Page not found</Typography>
                        <Typography>
                          The page you're looking for doesn't exist.
                        </Typography>
                      </Box>
                    }
                  />
                </Routes>
              </Box>
            </Container>
          )}
        </ErrorBoundary>
      </Router>
    </ThemeProvider>
  )
}

export default App
